import { useEffect, useRef, useState } from 'react'

const ANIMATION_DURATION_MS = 2000

/** easeOutCubic: empieza rápido y suaviza al final */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function lerpNumber(start: number, end: number, progress: number): number {
  const t = easeOutCubic(progress)
  return Math.round(start + (end - start) * t)
}

function lerpNumbers<T extends Record<string, number>>(
  start: T,
  end: T,
  progress: number
): T {
  const result = {} as T
  for (const key of Object.keys(end) as (keyof T)[]) {
    result[key] = lerpNumber(start[key], end[key], progress) as T[keyof T]
  }
  return result
}

function numbersEqual<T extends Record<string, number>>(a: T, b: T): boolean {
  return (Object.keys(b) as (keyof T)[]).every((key) => a[key] === b[key])
}

function createZeroState<T extends Record<string, number>>(
  keys: (keyof T)[]
): T {
  const zero = {} as T
  for (const key of keys) {
    zero[key] = 0 as T[keyof T]
  }
  return zero
}

/**
 * Interpola valores numéricos hacia los targets con easeOutCubic (2s por defecto).
 * Mismo efecto que la card de avance exportada desde lms-components.
 */
export function useAnimatedNumbers<T extends Record<string, number>>(
  targets: T,
  duration = ANIMATION_DURATION_MS
): T {
  const keysRef = useRef<(keyof T)[]>(Object.keys(targets) as (keyof T)[])
  const targetsRef = useRef(targets)
  targetsRef.current = targets
  const targetsKey = JSON.stringify(targets)

  const [displayed, setDisplayed] = useState<T>(() =>
    createZeroState(keysRef.current)
  )
  const displayedRef = useRef(displayed)
  const rafIdRef = useRef<number | null>(null)
  const startRef = useRef<T>(displayed)

  displayedRef.current = displayed

  useEffect(() => {
    const currentTargets = targetsRef.current
    const current = displayedRef.current
    if (numbersEqual(current, currentTargets)) return

    startRef.current = { ...current }
    const startTime = performance.now()

    const tick = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      setDisplayed(lerpNumbers(startRef.current, currentTargets, progress))
      if (progress < 1) {
        rafIdRef.current = requestAnimationFrame(tick)
      }
    }

    rafIdRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [targetsKey, duration])

  return displayed
}
